import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, onIpNotAllowed, onUnauthenticated } from "../api/client.ts";
import type { Me, Permission } from "../api/types.ts";
import { resetAssetCache } from "./assets.ts";

type SessionCtx = {
  me: Me | null;
  loading: boolean;
  /** Set when the server answered 403 ip_not_allowed: the whole console shows a blocked state. */
  blocked: string | null;
  /** Set after login / invite enrolment / joining (the server returns `Me`). */
  setMe: (me: Me | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** Make another brand active; pages remount and load that brand's data. */
  switchMerchant: (merchantId: string) => Promise<Me>;
  can: (p: Permission) => boolean;
  canAny: (...p: Permission[]) => boolean;
};

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState<string | null>(null);

  const setMe = useCallback((m: Me | null) => {
    setMeState((old) => {
      if (old?.merchant.id !== m?.merchant.id) resetAssetCache();
      return m;
    });
    if (m) setBlocked(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<Me>("/auth/me"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else throw e;
    }
  }, [setMe]);

  useEffect(() => {
    refresh().catch(() => setMe(null)).finally(() => setLoading(false));
  }, [refresh, setMe]);

  // Any 401 `unauthenticated` anywhere → drop the user; RequireAuth sends them to /login.
  useEffect(() => onUnauthenticated(() => setMe(null)), [setMe]);
  useEffect(() => onIpNotAllowed((msg) => setBlocked(msg || "access from this network is not allowed")), []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setMe(null);
      setBlocked(null);
    }
  }, [setMe]);

  const switchMerchant = useCallback(
    async (merchantId: string) => {
      const m = await api.post<Me>("/auth/switch", { merchant_id: merchantId });
      setMe(m);
      return m;
    },
    [setMe],
  );

  const value = useMemo<SessionCtx>(() => {
    const has = (p: Permission) => !!me?.permissions.includes(p);
    return { me, loading, blocked, setMe, refresh, logout, switchMerchant, can: has, canAny: (...ps) => ps.some(has) };
  }, [me, loading, blocked, setMe, refresh, logout, switchMerchant]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/** The logged-in user (only use under RequireAuth). */
export function useMe(): Me {
  const { me } = useSession();
  if (!me) throw new Error("useMe without a session");
  return me;
}
